/**
 * Customer review widget (Sprint 91).
 * Appears on tracking page when order is delivered AND customer hasn't reviewed.
 * Compact 5-star + comment.
 */
import { useEffect, useState } from "react";
import { Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";

export default function ReviewWidget({ trackingId, status, isOwner }) {
  const [existing, setExisting] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!trackingId) return;
    api.get(`/reviews/by-order/${trackingId}`)
      .then(({ data }) => { if (!cancelled) setExisting(data.review); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [trackingId]);

  const delivered = ["delivered", "completed"].includes(String(status || "").toLowerCase());
  if (!delivered || !isOwner) return null;

  if (existing || submitted) {
    const r = existing || { rating, comment };
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mt-4" data-testid="review-thanks">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <p className="text-sm font-semibold text-emerald-900">Thanks for your review!</p>
        </div>
        <div className="flex items-center gap-1">
          {[1,2,3,4,5].map(i => <Star key={i} className={`h-4 w-4 ${i <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
        </div>
        {r.comment && <p className="text-xs text-emerald-800 mt-1">&ldquo;{r.comment}&rdquo;</p>}
      </div>
    );
  }

  const submit = async () => {
    if (rating < 1) { toast.error("Pick a rating"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { tracking_id: trackingId, rating, comment: comment.trim() || null });
      setSubmitted(true);
      toast.success("Thanks — your review is saved!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mt-4" data-testid="review-widget">
      <p className="text-sm font-semibold text-slate-900 mb-2">How was your delivery?</p>
      <div className="flex items-center gap-1 mb-3">
        {[1,2,3,4,5].map(i => (
          <button
            key={i}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setRating(i)}
            className="p-1"
            data-testid={`review-star-${i}`}
          >
            <Star className={`h-6 w-6 transition-colors ${i <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, 600))}
        placeholder="What stood out? (optional)"
        rows={2}
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mb-2"
        data-testid="review-comment"
      />
      <button
        onClick={submit}
        disabled={submitting || rating < 1}
        className="px-4 py-2 rounded-lg bg-[#FF6B00] text-white text-sm font-semibold hover:bg-[#E55A00] disabled:opacity-60"
        data-testid="review-submit"
      >
        {submitting ? "Sending…" : "Submit review"}
      </button>
    </div>
  );
}
